import { ChartOptions, InteractionModeMap } from "chart.js";
import { x, y, yCap, yMax, yMin } from "../Scales/index.js";
import { translatePropertyTitle } from "../translationHelper.js";
import { legend } from "../Plugins/index.js";
import {
	legendValueLabel,
	residualCapacityLabel,
	totalAmountLabel,
} from "../Labels/index.js";
import { GRAPH_MIRROR_DIRECTIONS } from "../Enums/index.js";
import {
	dimensionRenderMode,
	hasAdditionalYearAxis,
	hasOffsetDimension,
	horizontalDateGranularity,
	horizontalSupportsPeriodTicks,
} from "../graphUtils.js";
import { ScaleWeekTicksPluginOptions } from "../Plugins/scaleWeekTicks.js";
import DocumentDO = App.Data.Explorer.DocumentDO;
import QuickFilterNodeDTO = App.Data.Explorer.Filter.QuickFilterNodeDTO;

export const baseOptions: ChartOptions = {
	locale: "nl-NL",
	animation: false,
	responsive: true,
	maintainAspectRatio: false,
	layout: {
		autoPadding: false,
		padding: {
			bottom: 0,
		},
	},
	events: [
		"mousemove",
		"mouseout",
		"click",
		"touchstart",
		"touchmove",
		"contextmenu",
	],
};

export function getScaleWeekTicksOptions(
	derivedDocument: DocumentDO
): ScaleWeekTicksPluginOptions | false {
	if (
		!horizontalSupportsPeriodTicks(derivedDocument) ||
		derivedDocument.is_horizontal_sorting_enabled
	) {
		return false;
	}

	return {
		scaleId: "x",
		granularity: horizontalDateGranularity(derivedDocument),
	};
}

export function documentToOptions(
	derivedDocument: DocumentDO,
	graphSettings?: any,
	isExplorer: boolean = false,
	isDarkMode: boolean = false,
	isRenderingForImage: boolean = false,
	hierarchyNodes: Record<number, QuickFilterNodeDTO> = {}
): ChartOptions {
	if (derivedDocument !== undefined) {
		const scales: ChartOptions["scales"] = {
			y: y(
				derivedDocument,
				translatePropertyTitle(derivedDocument.vertical),
				isDarkMode
			),
			x: x(
				derivedDocument,
				isDarkMode,
				dimensionRenderMode(derivedDocument.horizontal_dimension),
				hasOffsetDimension(derivedDocument) &&
					derivedDocument.graph_type === "bar",
				graphSettings?.shouldShowConfigurationText ?? true, // if graph settings are not provided (in the explorer), we default to true.
				graphSettings?.showOnlyOldSnapshotDate ?? false,
				hierarchyNodes
			),
			yCap: yCap(derivedDocument),
			yMax: yMax(derivedDocument),
			yMin: yMin(derivedDocument),
		};

		if (derivedDocument.secondary_vertical) {
			scales["ySecondary"] = y(
				derivedDocument,
				translatePropertyTitle(derivedDocument.secondary_vertical),
				isDarkMode,
				true
			);
		}

		let mode: keyof InteractionModeMap = "nearest";
		if (derivedDocument.graph_type === "bar") {
			mode = "point";
		} else if (derivedDocument.graph_type === "area") {
			mode = "nearestArea";
		}

		return {
			...baseOptions,
			// think about creating a Custom Interaction Mode (https://www.chartjs.org/docs/latest/configuration/interactions.html#modes)
			interaction: {
				mode: mode,
				axis: mode === "point" ? "x" : "xy",
				intersect: mode !== "nearestArea",
				includeInvisible: true,
			},
			scales,
			devicePixelRatio: isRenderingForImage ? 4 : window.devicePixelRatio,
			plugins: {
				dragData: {
					dragX: false,
					dragY: false,
				},
				legend: legend(
					derivedDocument,
					derivedDocument.legend_position,
					derivedDocument.is_legend_visible,
					isDarkMode
				),
				legendPlugin: {
					isApplyFilterEnabled:
						!!derivedDocument?.legend_dimension &&
						isExplorer &&
						derivedDocument?.legend_dimension.is_filterable,
					legendDimension: derivedDocument?.legend_dimension,
					derivedDocument: derivedDocument,
				},
				fixedLegendSizePlugin: {
					fixedWidth: !isExplorer ? derivedDocument.legend_width : null,
				},
				datalabels: {
					display: "auto",
					labels: {
						legendValue: legendValueLabel(derivedDocument, isDarkMode),
						totalAmountDirectionUp: totalAmountLabel(
							derivedDocument,
							isDarkMode,
							GRAPH_MIRROR_DIRECTIONS.UP
						),
						totalAmountDirectionDown: totalAmountLabel(
							derivedDocument,
							isDarkMode,
							GRAPH_MIRROR_DIRECTIONS.DOWN
						),
						residualCapacity: residualCapacityLabel(
							derivedDocument,
							isDarkMode
						),
					},
				},
				tooltip: false,
				yearLabels: {
					baseAxisId: "x",
					isActive: hasAdditionalYearAxis(derivedDocument),
					offsetY: 20,
				},
				snapshotDate: {
					date: derivedDocument.period_measurement_parsed,
					derivedDocument: derivedDocument,
					showOnlyOldSnapshotDate:
						graphSettings?.showOnlyOldSnapshotDate ?? false,
				},
				scaleWeekTicks: getScaleWeekTicksOptions(derivedDocument),
				secondLevelLabels: {
					hasOffset: derivedDocument.offset_dimension !== null,
					document: derivedDocument,
				},
			},
		};
	}
	return {};
}
